import { frameworkContent, frameworkContentDe } from '../data';
import type { Locale } from '../i18n';

export interface ProjectInput {
  idea: string;
  industry?: string;
  location?: string;
  teamSize?: string;
  stage?: string;
  goals?: string;
}

function getContexts(locale: Locale) {
  const data = locale === 'de' ? frameworkContentDe : frameworkContent;
  return {
    namingContext: data.naming.naming_methodology + '\n\n' + data.naming.industry_naming_matrix,
    legalContext:
      data.legal.entity_selection_framework +
      '\n\n' +
      data.legal.jurisdiction_matrix +
      '\n\n' +
      data.legal.international_holding_structures +
      '\n\n' +
      data.legal.compliance_checklist,
    brandContext:
      data.brandIdentity.brand_positioning_template +
      '\n\n' +
      data.brandIdentity.visual_identity_brief +
      '\n\n' +
      data.brandIdentity.tone_of_voice_guide,
    sopContext: data.sops.sop_template + '\n\n' + data.sops.sop_index,
    governanceContext: data.governance.document_control_policy + '\n\n' + data.governance.approval_matrix,
  };
}

function languageRule(locale: Locale): string {
  return locale === 'de'
    ? 'Respond entirely in German (Deutsch). Verwende professionelle Geschäftssprache.'
    : 'Respond entirely in English.';
}

function baseContext(input: ProjectInput, locale: Locale): string {
  const isDe = locale === 'de';
  const labels = isDe
    ? {
        idea: 'Geschäftsidee / Konzept',
        industry: 'Branche',
        location: 'Hauptsitz / Rechtsordnung',
        teamSize: 'Teamgröße / Phase',
        stage: 'Unternehmensphase',
        goals: 'Ziele',
        fallback: 'Nicht angegeben',
      }
    : {
        idea: 'Idea / Concept',
        industry: 'Industry',
        location: 'Primary Location / Jurisdiction',
        teamSize: 'Team Size / Stage',
        stage: 'Business Stage',
        goals: 'Goals',
        fallback: 'Not specified',
      };

  return `You are an expert company builder, operations consultant, and brand strategist. Use the provided framework templates and checklists to produce structured, actionable output. ${languageRule(locale)}

## ${isDe ? 'Projektdaten' : 'Project Input'}

- **${labels.idea}**: ${input.idea || labels.fallback}
- **${labels.industry}**: ${input.industry || labels.fallback}
- **${labels.location}**: ${input.location || labels.fallback}
- **${labels.teamSize}**: ${input.teamSize || labels.fallback}
- **${labels.stage}**: ${input.stage || labels.fallback}
- **${labels.goals}**: ${input.goals || labels.fallback}

${isDe ? 'Leite vernünftige Annahmen ab, wenn die Eingaben unklar sind. Sei spezifisch und praktisch. Ausgabe in Markdown.' : 'Infer reasonable defaults where the input is vague. Be specific and practical. Output in Markdown.'}
`;
}

export function companySetupPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const { legalContext } = getContexts(locale);
  const isDe = locale === 'de';
  return `${baseContext(input, locale)}

## ${isDe ? 'Framework-Kontext' : 'Framework Context'}

${legalContext}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle einen **Firmenaufbau-Guide** in Markdown. Enthalten sein sollen:

1. **Executive Summary** — ein Absatz zur Beschreibung des Vorhabens.
2. **Empfohlene Rechtsform** — Unternehmensform und Rechtsordnung(en) mit Begründung anhand Standort, Haftung, Steuern und Kapitalbedarf.
3. **Meilensteine im ersten Jahr** — 90-Tage-, 6-Monats- und 12-Monats-Ziele.
4. **Sofortmaßnahmen** — Gründung, Geschäftskonto, IP-Anmeldung, Buchhaltung etc.
5. **Top-Risiken und Absicherung** — die drei wichtigsten Risiken für diesen Geschäftstyp.

Verwende Tabellen, wo sie helfen. Keine Rechtsberatung; formuliere als Empfehlungen zur Prüfung mit einem Anwalt.`
    : `Create a **Company Setup Guide** in Markdown. Include:

1. **Executive Summary** — one-paragraph description of the venture.
2. **Recommended Legal Structure** — entity type and jurisdiction(s) with rationale based on location, liability, tax, and capital needs.
3. **First-Year Milestones** — 90-day, 6-month, and 12-month goals.
4. **Immediate Action Items** — incorporate, open bank account, register IP, set up accounting, etc.
5. **Key Risks and Mitigations** — top 3 risks for this type of business.

Use tables where helpful. Do not give legal advice; frame as recommendations to review with counsel.`
}
`;
}

export function brandIdentityPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const { namingContext } = getContexts(locale);
  const { brandContext } = getContexts(locale);
  const isDe = locale === 'de';
  return `${baseContext(input, locale)}

## ${isDe ? 'Framework-Kontext' : 'Framework Context'}

${namingContext}

${brandContext}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle einen **Markenidentitäts-Brief** in Markdown. Enthalten sein sollen:

1. **Markenpositionierung** — Für [Zielgruppe] ist [Marke] die [Kategorie], die [Kernnutzen] bietet, weil [Glaubwürdigkeit].
2. **Namensideen** — 8–12 Kandidaten anhand der Branchen-Matrix, mit Begründung und Risikohinweisen.
3. **Namensbewertung** — Bewerte die Top-3 auf Merkfähigkeit, Aussprache, Unterscheidungskraft, Relevanz, rechtliche Verfügbarkeit und linguistische Sicherheit.
4. **Visuelle Richtung** — Farbpalette (Hex), Typografie, Bildsprache und Logo-Ansatz.
5. **Tone of Voice** — 3–5 Stimmenprinzipien mit „Wir sind / Wir sind nicht“-Beispielen.
6. **Tagline-Optionen** — 3 Varianten.

Sei kreativ, aber verankert in Branche und Zielgruppe.`
    : `Create a **Brand Identity Brief** in Markdown. Include:

1. **Brand Positioning Statement** — For [target audience], [brand] is the [category] that [key benefit] because [reason to believe].
2. **Name Ideas** — 8–12 candidate names following the industry naming matrix, with rationale and risk notes.
3. **Name Evaluation** — score the top 3 names on memorability, pronounceability, distinctiveness, relevance, legal availability, and linguistic safety.
4. **Visual Direction** — color palette (hex codes), typography direction, imagery style, and logo approach.
5. **Tone of Voice** — 3–5 voice principles with examples of "we are / we are not."
6. **Tagline Options** — 3 options.

Be creative but grounded in the industry and audience.`
}
`;
}

export function softwareStackPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const isDe = locale === 'de';
  return `${baseContext(input, locale)}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle einen **Empfohlenen Software-Stack** in Markdown. Strukturiere nach Geschäftsfunktion:

1. **Unternehmen / Recht** — Gründung, Verträge, E-Signatur, IP-Tracking.
2. **Finanzen / Buchhaltung** — Buchhaltung, Payroll, Spesen, Rechnungen.
3. **HR / People** — HRIS, Recruiting, Payroll/Benefits, Performance, Zeiterfassung.
4. **Operations** — Projektmanagement, Inventar, Beschaffung, Lieferantenmanagement.
5. **Marketing** — CRM, E-Mail, Social, Design, Analytics.
6. **Sales** — CRM, Angebote, Terminplanung.
7. **IT / Sicherheit** — Identity, Device Management, Passwortmanager, Cloud-Speicher, Backup.
8. **Kommunikation** — Team-Chat, Video, Docs/Wiki.

Pro Kategorie: Primary Recommendation (Tool + Begründung), budgetfreundliche Alternative, Enterprise-Alternative, wann einführen (Phase / Teamgröße). Füge eine Starter-Stack-Tabelle für die ersten 10 Mitarbeiter hinzu.`
    : `Create a **Recommended Software Stack** in Markdown. Organize by business function:

1. **Company / Legal** — incorporation, contracts, e-signature, IP tracking.
2. **Finance / Accounting** — accounting, payroll, expense management, invoicing.
3. **HR / People** — HRIS, recruiting, payroll/benefits, performance, time tracking.
4. **Operations** — project management, inventory, procurement, vendor management.
5. **Marketing** — CRM, email, social, design, analytics.
6. **Sales** — CRM, proposals, scheduling.
7. **IT / Security** — identity, device management, password manager, cloud storage, backup.
8. **Communication** — team chat, video, docs/wiki.

For each category provide: Primary recommendation (tool name, one-line why), Budget-friendly alternative, Enterprise alternative, When to adopt (stage / team size). Add a starter stack table for the first 10 employees.`
}
`;
}

export function sopsPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const { sopContext } = getContexts(locale);
  const isDe = locale === 'de';
  const data = locale === 'de' ? frameworkContentDe : frameworkContent;
  return `${baseContext(input, locale)}

## ${isDe ? 'Framework-Kontext' : 'Framework Context'}

${sopContext}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle eine **SOP-Roadmap** in Markdown. Enthalten sein sollen:

1. **Prioritäre SOPs für dieses Unternehmen** — 12–16 SOPs, die für Branche/Phase besonders relevant sind, nach Funktion gegliedert.
2. **Top-5-SOP-Gliederungen** — für jede eine vollständige SOP mit Zweck, Geltungsbereich, Rollen, Ablauf, Inputs/Outputs, Kennzahlen und Freigabe.
3. **Implementierungsreihenfolge** — Phasierung über 30/60/90 Tage.

Zu befolgendes SOP-Template:

${data.sops.sop_template}`
    : `Create a **Standard Operating Procedures (SOPs) Roadmap** in Markdown. Include:

1. **Priority SOPs for This Business** — list 12–16 SOPs most relevant to this industry/stage, mapped to function.
2. **Top 5 SOP Outlines** — for each, write a full SOP using the template below with Purpose, Scope, Roles, Procedure steps, Inputs/Outputs, Metrics, and Approval.
3. **Implementation Order** — phase them across 30/60/90 days.

SOP Template to follow:

${data.sops.sop_template}`
}
`;
}

export function legalCompliancePrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const { legalContext } = getContexts(locale);
  const { governanceContext } = getContexts(locale);
  const isDe = locale === 'de';
  return `${baseContext(input, locale)}

## ${isDe ? 'Framework-Kontext' : 'Framework Context'}

${legalContext}

${governanceContext}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle eine **Rechts- und Compliance-Übersicht** in Markdown. Enthalten sein sollen:

1. **Rechtsform und Eigentümerstruktur** — Empfohlene Struktur, Rechtsordnung, Eigentümergliederung.
2. **Benötigte Verträge** — 8–12 Verträge mit Priorität und Zeitpunkt.
3. **Datenschutz / Sicherheit** — anwendbare Regelungen (DSGVO, CCPA etc.), Datenschutzerklärung, AVV, Incident-Response-Trigger.
4. **Geistiges Eigentum** — Marken, Urheberrecht, Patente, Domains, IP-Zuweisungen.
5. **Arbeitsrecht** — Klassifizierung, Verträge, lokale Pflichten.
6. **Branchenspezifische Lizenzen** — Genehmigungen, Anmeldungen, Compliance-Anforderungen.
7. **Governance-Dokumente** — benötigte Richtlinien und Freigabematrix im ersten Jahr.

Verwende Checklisten und Tabellen. Formuliere als Roadmap zur Prüfung mit qualifiziertem Rechtsberater.`
    : `Create a **Legal and Compliance Map** in Markdown. Include:

1. **Entity and Ownership** — recommended structure, jurisdiction, and ownership outline.
2. **Key Contracts Needed** — list 8–12 contracts with priority and when to put them in place.
3. **Data Privacy / Security** — applicable regulations (GDPR, CCPA, etc.), privacy policy needs, data processing agreements, incident response triggers.
4. **Intellectual Property** — trademark, copyright, patent, domain, IP assignments needed.
5. **Employment / Labor** — worker classification, contracts, local employment obligations.
6. **Industry-Specific Licensing** — permits, registrations, or compliance requirements for this industry.
7. **Governance Documents** — policies and approval matrix needed in the first year.

Use checklists and tables. Frame as a roadmap to review with qualified counsel.`
}
`;
}

export function orgChartRolesPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const isDe = locale === 'de';
  return `${baseContext(input, locale)}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle einen **Organigramm- und Rollenplan** in Markdown. Enthalten sein sollen:

1. **Gründungsteam / erste 5 Rollen** — Rollen, Verantwortlichkeiten und empfohlene Einstellungsreihenfolge.
2. **Organigramm bei 10, 25 und 50 Mitarbeitern** — für jede Größe ein visueller Baum in einem HTML-Block mit der Klasse <code>org-tree</code>. Verwende verschachtelte ungeordnete Listen (<code>ul / li</code>), bei denen jede Rolle in einer Karte dargestellt wird. Beispiel:
   <div class="org-tree">
     <ul>
       <li><strong>CEO / Gründer</strong>
         <ul>
           <li><strong>CTO</strong>
             <ul>
               <li>Software Engineer</li>
               <li>Product Designer</li>
             </ul>
           </li>
           <li><strong>Head of Sales</strong></li>
         </ul>
       </li>
     </ul>
   </div>
3. **Wichtige Positionen** — 5–8 Rollenprofile mit:
   - Rollentitel
   - Vorgesetzter
   - Kernaufgaben
   - Must-have-Fähigkeiten
   - Einstellungszeitpunkt (Phase / Trigger)
4. **Einstellungssequenz** — Priorisierung nach Geschäftsmodell.

Verwende Tabellen und Aufzählungen außerhalb der org-tree-Blöcke.`
    : `Create an **Organization Chart and Important Positions** plan in Markdown. Include:

1. **Founding Team / First 5 Roles** — roles, responsibilities, and suggested order of hire.
2. **Org Chart at 10, 25, and 50 Employees** — for each size, provide a visual tree inside an HTML block with the class <code>org-tree</code>. Use nested unordered lists (<code>ul / li</code>) where every role appears as a card. Example:
   <div class="org-tree">
     <ul>
       <li><strong>CEO / Founder</strong>
         <ul>
           <li><strong>CTO</strong>
             <ul>
               <li>Software Engineer</li>
               <li>Product Designer</li>
             </ul>
           </li>
           <li><strong>Head of Sales</strong></li>
         </ul>
       </li>
     </ul>
   </div>
3. **Key Position Descriptions** — write 5–8 role outlines covering: Role title, Reports to, Core responsibilities, Must-have skills, When to hire (stage / trigger).
4. **Hiring Sequence Recommendation** — prioritize which roles to fill first, second, third based on the business model.

Use tables and bullet lists outside of the org-tree blocks.`
}
`;
}

export function employeeOutlinesPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const isDe = locale === 'de';
  const data = locale === 'de' ? frameworkContentDe : frameworkContent;
  return `${baseContext(input, locale)}

## ${isDe ? 'Framework-Kontext' : 'Framework Context'}

${data.sops.hr['sop-hr-001-recruitment']}

${data.sops.hr['sop-hr-002-onboarding']}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle **Mitarbeiterprofile** in Markdown. Enthalten sein sollen:

1. **Einstufungsbänder** — 3–5 Level (z. B. Junior, Professional, Senior, Lead, Executive) mit typischem Verantwortungsbereich.
2. **Vergütungsansatz** — Gehaltsbänder, Equity/Variable, Benefits-Baseline.
3. **Standard-Onboarding-Checkliste** — erster Tag, erste Woche, 30/60/90 Tage.
4. **Rollenvorlagen** — für die 3 wichtigsten ersten Einstellungen:
   - Jobtitel
   - Mission der Rolle
   - Kernaufgaben
   - Erforderliche Skills / Erfahrung
   - Erfolgskennzahlen (30/60/90)
   - Vergütungsband

Verwende Tabellen zur besseren Lesbarkeit.`
    : `Create **Employee Outlines** in Markdown. Include:

1. **Employment Levels / Bands** — 3–5 levels (e.g., Junior, Mid, Senior, Lead, Executive) with typical scope.
2. **Compensation Approach** — salary ranges, equity/variable pay philosophy, benefits baseline.
3. **Standard Onboarding Checklist** — first day, first week, 30/60/90 days.
4. **Role Templates** — for the 3 most important early hires, provide: Job title, Mission of the role, Key responsibilities, Required skills / experience, Success metrics (30/60/90), Compensation band.

Use tables where it improves readability.`
}
`;
}

export function scalingFrameworkPrompt(input: ProjectInput, locale: Locale = 'en'): string {
  const isDe = locale === 'de';
  return `${baseContext(input, locale)}

## ${isDe ? 'Aufgabe' : 'Task'}

${
  isDe
    ? `Erstelle einen **Scaling-Rahmen** in Markdown. Enthalten sein sollen:

1. **Wachstumsphasen** — 4–5 Phasen (z. B. Idee, Launch, Product-Market Fit, Skalierung, Optimierung) mit:
   - Phasenname und Ziel
   - Typische Headcount-Spanne
   - Umzusetzende Systeme
   - Kritische Einstellungen
   - Governance-Checkpoints
2. **Systeme pro Phase** — Finanzen, HR, Operations, Sales, Marketing, IT.
3. **Entscheidungs-Trigger** — wann gründen, ersten Mitarbeiter einstellen, neuen Markt öffnen, Kapital aufnehmen, umstrukturieren.
4. **Kennzahlen pro Phase** — 3–5 Kennzahlen pro Stufe.
5. **Typische Fallstricke und Vermeidung** — 5 Fallstricke für diesen Geschäftstyp.

Verwende eine Phasen-Tabelle und handlungsleitende Checklisten.`
    : `Create a **Scaling Framework** in Markdown. Include:

1. **Growth Phases** — define 4–5 phases (e.g., Idea, Launch, Product-Market Fit, Scale, Optimize) with: Phase name and goal, Typical headcount range, Key systems to implement, Critical hires, Governance checkpoints.
2. **Systems to Implement by Phase** — finance, HR, operations, sales, marketing, IT.
3. **Decision Triggers** — when to incorporate, hire first employee, open new market, raise capital, restructure.
4. **Key Metrics by Phase** — 3–5 metrics to track at each stage.
5. **Common Pitfalls and How to Avoid Them** — 5 pitfalls for this type of business.

Use a phase table and actionable checklists.`
}
`;
}

export const sectionPrompts = {
  companySetup: companySetupPrompt,
  brandIdentity: brandIdentityPrompt,
  softwareStack: softwareStackPrompt,
  sops: sopsPrompt,
  legalCompliance: legalCompliancePrompt,
  orgChartRoles: orgChartRolesPrompt,
  employeeOutlines: employeeOutlinesPrompt,
  scalingFramework: scalingFrameworkPrompt,
};

export type SectionKey = keyof typeof sectionPrompts;

export const sectionLabels: Record<SectionKey, string> = {
  companySetup: 'Company Setup',
  brandIdentity: 'Brand Identity',
  softwareStack: 'Software Stack',
  sops: 'SOPs',
  legalCompliance: 'Legal & Compliance',
  orgChartRoles: 'Org Chart & Roles',
  employeeOutlines: 'Employee Outlines',
  scalingFramework: 'Scaling Framework',
};

export const sectionLabelsDe: Record<SectionKey, string> = {
  companySetup: 'Firmenaufbau',
  brandIdentity: 'Markenidentität',
  softwareStack: 'Software-Stack',
  sops: 'SOPs',
  legalCompliance: 'Recht & Compliance',
  orgChartRoles: 'Organigramm & Rollen',
  employeeOutlines: 'Mitarbeiterprofile',
  scalingFramework: 'Scaling-Rahmen',
};

export function getSectionLabels(locale: Locale): Record<SectionKey, string> {
  return locale === 'de' ? sectionLabelsDe : sectionLabels;
}

export const sectionOrder: SectionKey[] = [
  'companySetup',
  'brandIdentity',
  'softwareStack',
  'sops',
  'legalCompliance',
  'orgChartRoles',
  'employeeOutlines',
  'scalingFramework',
];
